import { ColumnProps } from "antd/lib/table";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { Link, useParams } from "react-router-dom";
import ScaleContext from "../../../../context/scale/context";
import { IScaleSlot } from "../../../../models/scale/response";
import Space from "../../../general/antd/space";
import DeleteBtn from "../../../general/table-components/actions/delete-btn";
import Table from "../../../general/table-components/table";
import Can from "../../../../utils/rbac/can";
import { Permissions } from "../../../../utils/Permissions/Permissions";
import styles from "./styles.module.scss";

interface IProps {}

const ScaleSlotsTable: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { slots, actions, loading } = useContext(ScaleContext);

  const columns: ColumnProps<IScaleSlot>[] = [
    {
      title: t("hotkey"),
      dataIndex: "hotkey",
      key: "hotkey",
      width: 90,
      align: "center",
    },
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      align: "center",
      width: 320,
      render: (name: string, record) => {
        const productId = record?.product?.id;

        // An unusable row is fixed on the product, so it links there.
        if (!productId) return name ?? "-";

        return <Link to={`/products/details/${productId}`}>{name ?? "-"}</Link>;
      },
    },
    {
      // The product's barcode in this store is what the scale prints. It comes
      // back empty when none is valid: the offending value lives on the product.
      title: t("code"),
      dataIndex: "code",
      key: "code",
      align: "center",
      width: 160,
      render: (code: string | null, record) =>
        record?.codeIsValid ? (
          code ?? "-"
        ) : (
          <Tooltip title={t("invalid_scale_code_hint")}>
            <Space>
              <WarningOutlined className={styles.blockingIcon} />
              <span className={styles.blockingValue}>{t("no_valid_code")}</span>
            </Space>
          </Tooltip>
        ),
    },
    {
      title: t("price_per_kilogram"),
      dataIndex: "pricePerKilogram",
      key: "pricePerKilogram",
      align: "center",
      width: 170,
      render: (value: number | null, record) => {
        // Zero is as unusable as nothing at all: a label priced zero lets the
        // goods leave for free, so the flag decides, never the number.
        if (!record?.priceIsValid) {
          return (
            <Tooltip title={t("invalid_scale_price_hint")}>
              <Space>
                <WarningOutlined className={styles.blockingIcon} />
                <span className={styles.blockingValue}>
                  {value != null ? value : t("no_price")}
                </span>
              </Space>
            </Tooltip>
          );
        }

        return value != null ? `${value} ${t("currency")} / ${t("kg")}` : "-";
      },
    },
    {
      title: t("actions"),
      dataIndex: "",
      key: "actions",
      align: "center",
      width: 100,
      render: (_: any, record) => {
        if (record?.state !== "occupied" || !record?.product?.id) return null;

        return (
          <Can perform={Permissions.CAN_MANAGE_SCALE_PRODUCTS}>
            <DeleteBtn
              confirmTitle={t("confirm_detach_scale_product")}
              onConfirm={async () => {
                await actions.detachProduct(
                  Number(id),
                  record.product?.id as number
                );
              }}
              loading={loading.includes("detach")}
            />
          </Can>
        );
      },
    },
  ];

  return (
    <Table<any>
      rowKey="hotkey"
      showPagination={false}
      columns={columns}
      dataSource={slots ?? []}
      loading={loading.includes("slots") || slots == undefined}
      size="small"
      className={styles.table}
      scroll={{ x: "max-content" }}
      rowClassName={(record: IScaleSlot) => {
        if (record?.blocksExport) return styles.blockingRow;
        return record?.state === "free" ? styles.freeRow : "";
      }}
    />
  );
};

export default ScaleSlotsTable;
